import { reverse } from 'named-urls'
import React from 'react'

import type { InventoryShipmentResponse } from '@components/hooks/useInventoryShipmentsQuery'
import { List, Tag } from '@fourthwall/components'
import { routing } from '@utils/routing'

import { OfferPreview } from './OfferPreview'
import * as S from './styled'

export type TagAppearance = Parameters<typeof Tag>[0]['appearance']

type InventoryShipmentProps = {
  data: InventoryShipmentResponse
  isLoading: boolean
}

const TAG_APPEARANCE_MAP: Record<InventoryShipmentResponse[number]['status'], TagAppearance> = {
  ACTION_REQUIRED: 'alert',
  CANCELLED: 'alert',
  ITEMS_RECEIVED: 'success',
  RECEIVING_IN_PROGRESS: 'default',
  WAITING_FOR_DELIVERY: 'default',
  WAITING_FOR_TRACKING: 'default',
}

const TAG_LABEL_MAP: Record<InventoryShipmentResponse[number]['status'], string> = {
  ACTION_REQUIRED: 'Action required',
  CANCELLED: 'Canceled',
  ITEMS_RECEIVED: 'Items received',
  RECEIVING_IN_PROGRESS: 'Receiving',
  WAITING_FOR_DELIVERY: 'Waiting for delivery',
  WAITING_FOR_TRACKING: 'Waiting for tracking',
}

const countItemsInShipment = (shipment: InventoryShipmentResponse[number]): number => {
  let itemCount = 0

  shipment.offers.forEach((offer) => {
    offer.variants.forEach((variant) => {
      itemCount += Number(variant.declaredQuantity)
    })
  })

  return itemCount
}

const firstAvailableImageUrl = (shipment: InventoryShipmentResponse[number]): string => {
  return shipment.offers.find((offer) => offer.imageUrl)?.imageUrl || ''
}

export const InventoryShipment = ({ data, isLoading }: InventoryShipmentProps) => (
  <List
    data={data}
    topBorder={false}
    isLoading={isLoading}
    getItemLink={(shipment: InventoryShipmentResponse[number]) => ({
      pathname: reverse(routing.settings.shipping.inventoryShipments.self, {
        shippingId: shipment.id,
      }),
      search: location.search,
    })}
    renderItem={(shipment: InventoryShipmentResponse[number]) => (
      <S.ShipmentContainer>
        <S.Info>
          <OfferPreview
            image={firstAvailableImageUrl(shipment)}
            imagesCount={shipment.offers.length}
          />
          <S.ShipmentDetails>
            <S.ShipmentName>Shipment #{shipment.friendlyId}</S.ShipmentName>
            <S.ShipmentItemsQuantity>
              {countItemsInShipment(shipment)} items
            </S.ShipmentItemsQuantity>
          </S.ShipmentDetails>
        </S.Info>
        <Tag
          label={TAG_LABEL_MAP[shipment.status]}
          appearance={TAG_APPEARANCE_MAP[shipment.status]}
        />
      </S.ShipmentContainer>
    )}
  />
)
